import type {Ref} from 'vue';
import {computed, ref} from 'vue';

import type {
  IPagination,
  IPaginationFilters,
} from '@/assets/core/assets/api/types';

import useRequestHook from '@/assets/core/assets/hooks/requestHook';

import type {IQueryObject} from '@/assets/core/assets/helpers/serialize';
import {serializeFromObjectToQueryString} from '@/assets/core/assets/helpers/serialize';
import type {
  IRequestPaginationHookCache,
  TRequestHookUrl,
} from '@/assets/core/assets/hooks/types';

const defaultPage = 1;
const defaultPageSize = 50;

const useRequestPaginationHook = <TGet, TFilters extends IQueryObject>(
  baseUrl: TRequestHookUrl,
  baseFilters?: TFilters,
  basePagination?: Omit<IPaginationFilters, 'page'>,
) => {
  const request = useRequestHook();

  const cache = ref<IRequestPaginationHookCache<TGet>>({});

  const requestCounter = ref(0);

  const currentFilters = ref<TFilters | undefined>(baseFilters) as Ref<
    TFilters | undefined
  >;
  const paginationFilters = ref<IPaginationFilters>({
    page: defaultPage,
    size: basePagination?.size ?? defaultPageSize,
  });

  const getUrlClear = computed(
    () => new URL(baseUrl).origin + new URL(baseUrl).pathname,
  );
  const getKeyCurrent = computed(() =>
    getKey(paginationFilters.value, currentFilters.value),
  );

  const getBaseFilters = computed(() => baseFilters);
  const getFilters = computed(() => currentFilters.value);
  const getPagination = computed(() => paginationFilters.value);
  const getTotal = computed(() => {
    if (!cache.value[getKeyCurrent.value]) return 0;
    return cache.value[getKeyCurrent.value].total ?? 0;
  });

  const getPage = computed(() => {
    if (!cache.value[getKeyCurrent.value]) return [];
    return (
      cache.value[getKeyCurrent.value].pages[paginationFilters.value.page] ?? []
    );
  });

  const getPageAll = computed(() => {
    if (!cache.value[getKeyCurrent.value]) return [];
    return Object.values(cache.value[getKeyCurrent.value].pages).flat(1) ?? [];
  });

  const getIsFullLoaded = computed(() => {
    const currentCache = cache.value[getKeyCurrent.value];
    const itemsCount = getPageAll.value.length;

    return (
      currentCache?.total === itemsCount ||
      paginationFilters.value.page * paginationFilters.value.size > itemsCount
    );
  });

  function getKey(pagination: IPaginationFilters, filters?: TFilters): string {
    const query: IQueryObject = {
      ...filters,
      size:
        pagination.size !== defaultPageSize ? pagination.size.toString() : [],
    };

    const stringQuery = serializeFromObjectToQueryString(query);
    return getUrlClear.value + (stringQuery ? `?${stringQuery}` : '');
  }

  function getRequestUrl(
    pagination: IPaginationFilters,
    filters?: TFilters,
  ): string {
    const key = getKey(pagination, filters);

    const query: IQueryObject = {
      page: pagination.page !== defaultPage ? pagination.page.toString() : [],
    };

    const stringQuery = serializeFromObjectToQueryString(query);

    const delimiter = new URL(key).search.length ? '&' : '?';
    return key + (stringQuery ? `${delimiter}${stringQuery}` : '');
  }

  const getFiltersWithCurrent = (filters?: TFilters) => {
    const obj = filters ?? currentFilters.value ?? ({} as TFilters);

    if ('size' in obj) delete obj.size;
    if ('page' in obj) delete obj.page;

    return obj;
  };
  const getPaginationWithCurrent = (
    pagination?: Partial<IPaginationFilters>,
  ) => ({
    page: pagination?.page ?? paginationFilters.value.page,
    size: pagination?.size ?? paginationFilters.value.size,
  });
  const getParamsWithCurrent = (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ) => ({
    filters: getFiltersWithCurrent(filters),
    pagination: getPaginationWithCurrent(pagination),
  });

  const isFiltersCurrent = (filters?: TFilters) => {
    return (
      JSON.stringify(currentFilters.value) === JSON.stringify(filters ?? {})
    );
  };
  const setFilters = (filters: TFilters) => (currentFilters.value = filters);
  const resetFilters = () => (currentFilters.value = undefined);

  const setPagination = (pagination: Partial<IPaginationFilters>) => {
    if (pagination.size != null) paginationFilters.value.size = pagination.size;
    if (pagination.page != null) paginationFilters.value.page = pagination.page;
  };
  const resetPagination = () =>
    (paginationFilters.value = {size: defaultPageSize, page: defaultPage});

  const loadRequest = async (
    url: TRequestHookUrl,
  ): Promise<IPagination<TGet>> => {
    return await request.get(url);
  };

  const loadPage = async (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    const currentRequestId = ++requestCounter.value;

    const params = getParamsWithCurrent(filters, pagination);

    const key = getKey(params.pagination, params.filters);
    const url = getRequestUrl(params.pagination, params.filters);

    const data = await loadRequest(url);

    if (currentRequestId !== requestCounter.value) {
      return cache.value[key]?.pages[params.pagination.page] ?? [];
    }

    cache.value[key] = cache.value[key] ?? {
      pages: {},
      total: data.total,
    };

    cache.value[key].pages[params.pagination.page] = data.items;

    currentFilters.value = params.filters;
    paginationFilters.value = params.pagination;

    return data.items;
  };

  const loadPageWithCache = async (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    const currentRequestId = ++requestCounter.value;

    const params = getParamsWithCurrent(filters, pagination);

    const key = getKey(params.pagination, params.filters);

    const list =
      cache.value[key] && cache.value[key].pages[params.pagination.page]
        ? cache.value[key].pages[params.pagination.page]
        : await loadPage(params.filters, params.pagination);

    if (currentRequestId !== requestCounter.value) {
      return list;
    }

    currentFilters.value = params.filters;
    paginationFilters.value = params.pagination;

    return list;
  };

  const loadFirstPage = async (): Promise<TGet[]> => {
    return await loadPage(currentFilters.value as TFilters, {
      ...paginationFilters.value,
      page: defaultPage,
    });
  };

  const loadNextPage = async (): Promise<TGet[]> => {
    if (getIsFullLoaded.value) return [];

    return await loadPage(currentFilters.value as TFilters, {
      ...paginationFilters.value,
      page: paginationFilters.value.page + 1,
    });
  };

  const loadAllPage = async (): Promise<TGet[]> => {
    let data = await loadFirstPage();

    while (!getIsFullLoaded.value && data.length !== 0) {
      data = await loadNextPage();
    }

    return getPageAll.value;
  };

  return {
    isLoading: request.isLoading,

    getIsFullLoaded,

    getFilters,
    getBaseFilters,
    getPagination,
    getTotal,
    getPage,
    getPageAll,

    setFilters,
    resetFilters,
    isFiltersCurrent,
    setPagination,
    resetPagination,
    loadPage,
    loadPageWithCache,
    loadAllPage,
    loadFirstPage,
    loadNextPage,
  };
};

export default useRequestPaginationHook;
